import React, { Fragment } from 'react';

import { useViewModel } from './useViewModel';

import styles from './personalisation-progress-bar.module.css';

/**
 * PersonalisationProgressBar component displays the progress bar for the personalisation journey.
 *
 * @param {Object} props - Component props
 * @param {number} props.totalSteps - Total number of steps
 * @param {number} props.currentStep - Current step index
 * @param {boolean} props.isResetting - Whether the progress bar is resetting
 * @returns {import('react').JSX.Element | null}
 */
export function PersonalisationProgressBar({ totalSteps, currentStep, isResetting }) {
  const {
    stepsArray,
    stepCountLabel,
    stepCountShortLabel,
    getStepStatus,
    progressBarClassName,
    stepIndicatorClassName,
    lineClassName
  } = useViewModel({
    totalSteps,
    currentStep,
    isResetting
  });

  if (totalSteps < 2) return null;

  return (
    <div className={styles['personalisation__progress']}>
      <div className={progressBarClassName} role="navigation" aria-label="Progress">
        {stepsArray.map((_, index) => {
          const status = getStepStatus(index);
          return (
            <Fragment key={index}>
              <div className={stepIndicatorClassName(status)} />
              {index < stepsArray.length - 1 && <div className={lineClassName(index)} />}
            </Fragment>
          );
        })}
      </div>
      {/*
       * The dots carry no text, so the only thing a screen reader heard was the landmark's
       * "Progress" label. role="status" announces "Step X of Y" as the customer moves between
       * steps; the compact "n/m" beside the dots (Figma V1) is the visible form of the same
       * count, hidden from assistive technology so it is not read twice.
       */}
      <p className="visually-hidden" role="status">
        {stepCountLabel}
      </p>
      <p className={styles['personalisation__progress-count'] + ' body-sm'} aria-hidden="true">
        {stepCountShortLabel}
      </p>
    </div>
  );
}
